import { ApiError, apiRequest, ErrorCode, getErrorMessage } from './client';

// Error codes and error-related type definitions moved to client.ts
export { ApiError, ErrorCode, getErrorMessage };

// Program type definition
export interface Program {
  id: number;
  since: string;
  until: string;
  duration: number;
  supplier?: string;
  playerId?: number; // Player ID
  createdAt?: string; // DB's created_at converted to camelCase for frontend
}

// Standard API response format type definition
export interface StandardResponse<T> {
  data: T;
  meta: {
    [key: string]: unknown;
    pagination?: {
      total: number;
      limit: number;
      offset: number;
    };
    message?: string;
  };
}

// Program list response type definition
export type ProgramListResponse = StandardResponse<Program[]>;

// Single program response type definition
export type ProgramResponse = StandardResponse<Program>;

// Create response type definition
export type CreateResponse = StandardResponse<{ id: number }>;

// Update/delete response type definition
export type ActionResponse = StandardResponse<{ message: string }>;

// Batch create response type definition
export type BatchCreateResponse = StandardResponse<Program[]>;

// Program create request type definition
export interface CreateProgramRequest {
  since: string;
  until: string;
  duration: number;
  supplier?: string;
  playerId?: number; // Player ID
}

// Program update request type definition
export interface UpdateProgramRequest {
  since?: string;
  until?: string;
  duration?: number;
  supplier?: string;
  playerId?: number; // Player ID
}

// Program batch create request type definition
export interface CreateProgramBatchRequest {
  startAt: string;  // ISO 8601 format
  timeRange: {
    startMinute: number;  // 0-59
    endMinute: number;    // 0-59
  };
  duration: number;  // seconds
  supplier: string;
  playerId?: number; // Player ID
}

/**
 * Get program list
 */
export const getPrograms = (startAt?: string, endAt?: string, supplier?: string): Promise<ProgramListResponse> => {
  return apiRequest<ProgramListResponse>({
    method: 'GET',
    path: '/cms/programs',
    params: { startAt, endAt, supplier },
  });
};

/**
 * Get program by ID
 */
export const getProgram = (id: number): Promise<ProgramResponse> => {
  return apiRequest<ProgramResponse>({
    method: 'GET',
    path: `/cms/programs/${id}`,
  });
};

/**
 * Create new program
 */
export const createProgram = (data: CreateProgramRequest): Promise<CreateResponse> => {
  return apiRequest<CreateResponse>({
    method: 'POST',
    path: '/cms/programs',
    data,
  });
};

/**
 * Update program
 */
export const updateProgram = (id: number, data: UpdateProgramRequest): Promise<ActionResponse> => {
  return apiRequest<ActionResponse>({
    method: 'PUT',
    path: `/cms/programs/${id}`,
    data,
  });
};

/**
 * Delete program
 */
export const deleteProgram = (id: number): Promise<ActionResponse> => {
  return apiRequest<ActionResponse>({
    method: 'DELETE',
    path: `/cms/programs/${id}`,
  });
};

/**
 * Batch create programs
 */
export const createProgramBatch = (data: CreateProgramBatchRequest): Promise<BatchCreateResponse> => {
  return apiRequest<BatchCreateResponse>({
    method: 'POST',
    path: '/cms/programs/batch',
    data,
  });
};
